/**
 * NavigationBar Component
 * Persistent 3-icon navigation bar for child app
 */

import { t, getCurrentLanguage } from '../i18n/config.js';
import { LibraryFilterBar } from './LibraryFilterBar.js';

export class NavigationBar {
  constructor(container, router, profileData) {
    this.container = container;
    this.router = router;
    this.profileData = profileData;
    this.currentRoute = null;
    this.isHidden = false;
    this.isFilterMode = false;
    this.currentLanguage = getCurrentLanguage();
    this.element = null;
    this.filterBar = new LibraryFilterBar();
  }

  /**
   * Render navigation bar
   */
  render() {
    const nav = document.createElement('nav');
    nav.className = 'navigation-bar';
    nav.setAttribute('role', 'navigation');
    nav.setAttribute('aria-label', t('child.navigation.mainNavigation') || 'Main navigation');

    const items = [
      {
        route: 'profile',
        type: 'profile',
        label: this.profileData
          ? this.profileData.firstName || this.profileData.name || 'Profile'
          : 'Profile',
        ariaLabel: t('child.navigation.profile') || 'Profile',
      },
      {
        route: 'library',
        type: 'icon-image',
        icon: 'library',
        ariaLabel: t('child.navigation.library') || 'Library',
      },
      {
        route: 'achievements',
        type: 'icon-image',
        icon: 'achievements',
        ariaLabel: t('child.navigation.achievements') || 'Achievements',
      },
      {
        route: 'settings',
        type: 'icon-image',
        icon: 'settings',
        ariaLabel: t('child.navigation.settings') || 'Settings',
      },
    ];

    items.forEach((item) => {
      const button = this.createNavItem(item);
      nav.appendChild(button);
    });

    this.container.appendChild(nav);
    this.element = nav;

    // Set initial active state
    if (this.router.getCurrentRoute()) {
      this.setActive(this.router.getCurrentRoute());
    }
  }

  /**
   * Create navigation item
   */
  createNavItem({ route, type, icon, label, ariaLabel }) {
    const button = document.createElement('button');
    button.className = type === 'profile' ? 'nav-item nav-item--profile' : 'nav-item';
    button.dataset.route = route;
    button.setAttribute('aria-label', ariaLabel || label);

    // Disable library, achievements, and settings if no profile is selected
    if (!this.profileData && (route === 'library' || route === 'achievements' || route === 'settings')) {
      button.disabled = true;
      button.classList.add('disabled');
      button.setAttribute('aria-disabled', 'true');
      button.title = t('child.navigation.selectProfileFirst') || 'Please select a profile first';
    }

    if (type === 'profile') {
      // Profile navigation item with avatar only (no name label)
      const isAdult = this.profileData?.type === 'adult';
      let avatarUrl = '/svg/branding/KassieAvatarNoBorder.svg'; // Default until profile selected

      if (this.profileData) {
        const profileAvatar = this.profileData.avatarUrl || this.profileData.avatar;
        if (profileAvatar) {
          // If avatarUrl is just a filename (e.g., "Boy5-1.svg"), construct full path
          avatarUrl = profileAvatar.startsWith('http') || profileAvatar.startsWith('/')
            ? profileAvatar
            : `/svg/components/${profileAvatar}`;
        } else if (isAdult) {
          // Adult with no avatar — use GrownUp fallback
          avatarUrl = '/svg/components/GrownUp-1.svg';
        }
      }

      button.innerHTML = `
        <div class="nav-avatar-container">
          <img src="${avatarUrl}" alt="${label}" class="nav-avatar-image" referrerpolicy="no-referrer" onerror="this.onerror=null;this.src='/svg/components/GrownUp-1.svg'" />
        </div>
      `;
    } else if (type === 'icon-image') {
      // Language-specific icon image
      const iconPath = this.getNavigationIcon(icon, this.currentLanguage);
      if (icon === 'settings') {
        button.innerHTML = `
          <div class="nav-icon-container">
            <img src="${iconPath}" alt="${ariaLabel}" class="nav-icon-image nav-icon-default" />
            <img src="/svg/icons/SettingsGearsYellow.svg" alt="${ariaLabel}" class="nav-icon-image nav-icon-hover" />
          </div>
        `;
      } else {
        button.innerHTML = `
          <div class="nav-icon-container">
            <img src="${iconPath}" alt="${ariaLabel}" class="nav-icon-image" />
          </div>
        `;
      }
    }

    button.addEventListener('click', () => this.navigate(route));

    return button;
  }

  /**
   * Get navigation icon URL
   */
  getNavigationIcon(icon, language) {
    // Afrikaans icons
    if (language === 'af') {
      if (icon === 'library') {
        return '/svg/icons/Tassieicon-3.svg';
      }
      if (icon === 'achievements') {
        return '/svg/icons/Trophyicon-2.svg';
      }
      if (icon === 'settings') {
        return '/svg/icons/SettingsGears.svg';
      }
    }

    // English placeholders (use same icons for now)
    if (icon === 'library') {
      return '/svg/icons/Tassieicon-3.svg';
    }
    if (icon === 'achievements') {
      return '/svg/icons/Trophyicon-2.svg';
    }
    if (icon === 'settings') {
      return '/svg/icons/SettingsGears.svg';
    }

    // Fallback
    return '/svg/branding/KassieAvatar.svg';
  }

  /**
   * Navigate to route
   */
  navigate(route) {
    // Gate: if no profile is selected and trying to go anywhere other than profile, show modal
    if (!this.profileData && route !== 'profile') {
      this._showSelectProfileModal();
      return;
    }

    if (route === 'settings') {
      // Settings opens as a modal overlay — don't navigate
      this.toggleSettingsModal();
      return;
    }
    this.router.navigate(route, this.profileData);
  }

  /**
   * Show modal warning that a profile must be selected first
   * @private
   */
  _showSelectProfileModal() {
    // Prevent stacking — if already visible, bail
    if (document.getElementById('kassie-select-profile-modal')) return;

    // Remember what had focus so it can be restored when the modal closes
    const triggerEl = document.activeElement;

    const overlay = document.createElement('div');
    overlay.id = 'kassie-select-profile-modal';
    overlay.className = 'select-profile-modal-overlay';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-labelledby', 'kassie-select-profile-message');

    const panel = document.createElement('div');
    panel.className = 'select-profile-modal-panel';
    panel.setAttribute('tabindex', '-1');

    const icon = document.createElement('div');
    icon.className = 'select-profile-modal__icon';
    icon.setAttribute('aria-hidden', 'true');
    icon.textContent = '👆';
    panel.appendChild(icon);

    const message = document.createElement('p');
    message.id = 'kassie-select-profile-message';
    message.className = 'select-profile-modal__message';
    message.textContent = t('child.navigation.selectProfileModal') || "Selekteer eers 'n profiel voor jy kan ingaan";
    panel.appendChild(message);

    const btn = document.createElement('button');
    btn.className = 'select-profile-modal__btn';
    btn.textContent = t('app.close') || 'Sluit';
    btn.addEventListener('click', () => this._closeSelectProfileModal(overlay, triggerEl));
    panel.appendChild(btn);

    overlay.appendChild(panel);
    document.body.appendChild(overlay);

    // Trigger enter animation on next frame, then move focus into the modal
    requestAnimationFrame(() => {
      overlay.classList.add('is-visible');
      btn.focus();
    });

    // Close on backdrop click
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) {
        this._closeSelectProfileModal(overlay, triggerEl);
      }
    });

    // Keyboard: Escape closes, Tab is trapped within the panel
    overlay._kbCleanup = this._trapModalFocus(overlay, panel, () =>
      this._closeSelectProfileModal(overlay, triggerEl)
    );
  }

  /**
   * Close the select-profile modal
   * @param {HTMLElement} overlay - the modal overlay to close
   * @param {HTMLElement} [triggerEl] - element to restore focus to
   * @private
   */
  _closeSelectProfileModal(overlay, triggerEl) {
    if (overlay._kbCleanup) {
      overlay._kbCleanup();
      overlay._kbCleanup = null;
    }

    overlay.classList.remove('is-visible');
    overlay.addEventListener('transitionend', () => overlay.remove(), { once: true });
    // Fallback removal if transition doesn't fire
    setTimeout(() => { if (overlay.parentNode) overlay.remove(); }, 300);

    // Return keyboard focus to whatever triggered the modal
    if (triggerEl && typeof triggerEl.focus === 'function') {
      triggerEl.focus();
    }
  }

  /**
   * Basic focus trap + Escape-to-close for a modal dialog.
   * Keeps Tab/Shift+Tab cycling within `panel` and calls `onClose` on Escape.
   * @param {HTMLElement} overlay - overlay to attach the keydown listener to (for scoping/debugging)
   * @param {HTMLElement} panel - panel containing the focusable elements to trap within
   * @param {Function} onClose - called when Escape is pressed
   * @returns {Function} cleanup function that removes the listener
   * @private
   */
  _trapModalFocus(overlay, panel, onClose) {
    const getFocusable = () =>
      Array.from(
        panel.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        )
      ).filter((el) => !el.disabled && el.offsetParent !== null);

    const handleKeydown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }

      if (e.key === 'Tab') {
        const focusable = getFocusable();
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeydown);
    return () => document.removeEventListener('keydown', handleKeydown);
  }

  /**
   * Toggle settings modal visibility
   */
  toggleSettingsModal() {
    // Remember what had focus so it can be restored when the modal closes
    const triggerEl = document.activeElement;

    const modal = document.getElementById('kassie-settings-modal');
    if (modal) {
      // Already exists — always open (settings button only opens, never closes)
      if (modal.style.display === 'none') {
        modal.style.display = 'flex';
        modal._triggerEl = triggerEl;
        // Re-trigger enter animation
        const panel = modal.querySelector('.settings-modal-panel');
        if (panel) {
          panel.classList.remove('settings-modal-panel--closing');
          panel.style.animation = 'none';
          panel.offsetHeight; // force reflow
          panel.style.animation = '';
          this._focusModalPanel(panel);
          // Re-attach the focus trap (cleared on the previous close)
          modal._kbCleanup = this._trapModalFocus(modal, panel, () => this._closeSettingsModal(modal));
        }
      }
      return;
    }

    // Create modal — lazy import SettingsScreen
    import('../views/SettingsScreen.js').then(({ SettingsScreen }) => {
      this._createSettingsModal(SettingsScreen, triggerEl);
    });
  }

  /**
   * Create the settings modal overlay
   * @param {Function} SettingsScreen - the SettingsScreen class
   * @param {HTMLElement} [triggerEl] - element to restore focus to on close
   * @private
   */
  _createSettingsModal(SettingsScreen, triggerEl) {
    const overlay = document.createElement('div');
    overlay.id = 'kassie-settings-modal';
    overlay.className = 'settings-modal-overlay';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-label', t('child.settings.title') || 'Instellings');
    overlay._triggerEl = triggerEl;

    const panel = document.createElement('div');
    panel.className = 'settings-modal-panel';
    panel.setAttribute('tabindex', '-1');

    // Drag handle (desktop/tablet only)
    const dragHandle = document.createElement('div');
    dragHandle.className = 'settings-modal-drag-handle';
    panel.appendChild(dragHandle);

    // Content container for SettingsScreen to render into
    const content = document.createElement('div');
    content.className = 'settings-modal-content';
    panel.appendChild(content);

    overlay.appendChild(panel);
    document.body.appendChild(overlay);

    // Render settings into the content container
    const settings = new SettingsScreen(content, this.router, this.profileData);
    settings.render();

    // Close on overlay background click
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) {
        this._closeSettingsModal(overlay);
      }
    });

    // Close button inside settings will hide the modal
    content.addEventListener('click', (e) => {
      if (e.target.closest('#settingsCloseBtn') || e.target.closest('.settings-close-btn')) {
        e.preventDefault();
        e.stopPropagation();
        this._closeSettingsModal(overlay);
      }
    });

    // Draggable on all devices (desktop + phone)
    this._makeDraggable(panel, dragHandle);

    // Start spinning the settings gear icon
    this._startGearSpin();

    // Keyboard: Escape closes, Tab is trapped within the panel
    overlay._kbCleanup = this._trapModalFocus(overlay, panel, () => this._closeSettingsModal(overlay));

    // Move focus into the modal
    this._focusModalPanel(panel);
  }

  /**
   * Move focus to the first focusable element inside a modal panel,
   * falling back to the panel itself.
   * @param {HTMLElement} panel
   * @private
   */
  _focusModalPanel(panel) {
    requestAnimationFrame(() => {
      const closeBtn = panel.querySelector('#settingsCloseBtn, .settings-close-btn');
      const target = closeBtn || panel;
      if (typeof target.focus === 'function') target.focus();
    });
  }

  /**
   * Close the settings modal with exit animation
   * @param {HTMLElement} overlay - The modal overlay element
   * @private
   */
  _closeSettingsModal(overlay) {
    if (overlay._kbCleanup) {
      overlay._kbCleanup();
      overlay._kbCleanup = null;
    }

    const panel = overlay.querySelector('.settings-modal-panel');
    if (panel) {
      panel.classList.add('settings-modal-panel--closing');
      panel.addEventListener('animationend', () => {
        overlay.style.display = 'none';
        panel.classList.remove('settings-modal-panel--closing');
      }, { once: true });
    } else {
      overlay.style.display = 'none';
    }

    // Stop spinning gear icon
    this._stopGearSpin();

    // Return keyboard focus to whatever triggered the modal (e.g. the settings nav icon)
    if (overlay._triggerEl && typeof overlay._triggerEl.focus === 'function') {
      overlay._triggerEl.focus();
    }
  }

  /**
   * Start spinning the settings gear icon (360deg over 5s, loop)
   * @private
   */
  _startGearSpin() {
    const settingsBtn = this.element?.querySelector('[data-route="settings"]');
    if (settingsBtn) {
      settingsBtn.classList.add('nav-item--spinning');
    }
  }

  /**
   * Stop spinning the settings gear icon
   * @private
   */
  _stopGearSpin() {
    const settingsBtn = this.element?.querySelector('[data-route="settings"]');
    if (settingsBtn) {
      settingsBtn.classList.remove('nav-item--spinning');
    }
  }

  /**
   * Make a panel draggable from anywhere except buttons/inputs
   * @private
   */
  _makeDraggable(panel, handle) {
    let isDragging = false;
    let offsetX = 0;
    let offsetY = 0;

    panel.style.cursor = 'grab';

    panel.addEventListener('pointerdown', (e) => {
      // Don't drag when clicking buttons, inputs, or interactive elements
      const tag = e.target.tagName.toLowerCase();
      if (tag === 'button' || tag === 'input' || tag === 'select' || tag === 'textarea') return;
      if (e.target.closest('button') || e.target.closest('a') || e.target.closest('input')) return;

      isDragging = true;
      panel.style.cursor = 'grabbing';
      const rect = panel.getBoundingClientRect();
      offsetX = e.clientX - rect.left;
      offsetY = e.clientY - rect.top;
      panel.style.position = 'fixed';
      panel.style.margin = '0';
      e.preventDefault();
    });

    document.addEventListener('pointermove', (e) => {
      if (!isDragging) return;
      panel.style.left = `${e.clientX - offsetX}px`;
      panel.style.top = `${e.clientY - offsetY}px`;
      panel.style.transform = 'none';
    });

    document.addEventListener('pointerup', () => {
      if (isDragging) {
        isDragging = false;
        panel.style.cursor = 'grab';
      }
    });
  }

  /**
   * Set active navigation item
   */
  setActive(route) {
    this.currentRoute = route;

    if (!this.element) return;

    const items = this.element.querySelectorAll('.nav-item');
    items.forEach((item) => {
      if (item.dataset.route === route) {
        item.classList.add('active');
        item.setAttribute('aria-current', 'page');
      } else {
        item.classList.remove('active');
        item.removeAttribute('aria-current');
      }
    });
  }

  /**
   * Update profile data
   */
  updateProfile(profileData) {
    this.profileData = profileData;

    if (!this.element) return;

    // Update profile navigation item
    const profileNavItem = this.element.querySelector('.nav-item--profile');
    if (profileNavItem && profileData) {
      const avatar = profileNavItem.querySelector('.nav-avatar-image');

      const profileAvatar = profileData.avatarUrl || profileData.avatar;
      let avatarUrl = '/svg/branding/KassieAvatarNoBorder.svg';
      if (profileAvatar) {
        avatarUrl = profileAvatar.startsWith('http') || profileAvatar.startsWith('/')
          ? profileAvatar
          : `/svg/components/${profileAvatar}`;
      }

      const name = profileData.firstName || profileData.name || 'Profile';

      avatar.src = avatarUrl;
      avatar.alt = name;
    }

    // Enable/disable library and achievements buttons based on profile selection
    const libraryBtn = this.element.querySelector('[data-route="library"]');
    const achievementsBtn = this.element.querySelector('[data-route="achievements"]');
    const settingsBtn = this.element.querySelector('[data-route="settings"]');

    if (profileData) {
      // Profile selected - enable buttons
      if (libraryBtn) {
        libraryBtn.disabled = false;
        libraryBtn.classList.remove('disabled');
        libraryBtn.removeAttribute('aria-disabled');
        libraryBtn.removeAttribute('title');
      }
      if (achievementsBtn) {
        achievementsBtn.disabled = false;
        achievementsBtn.classList.remove('disabled');
        achievementsBtn.removeAttribute('aria-disabled');
        achievementsBtn.removeAttribute('title');
      }
      if (settingsBtn) {
        settingsBtn.disabled = false;
        settingsBtn.classList.remove('disabled');
        settingsBtn.removeAttribute('aria-disabled');
        settingsBtn.removeAttribute('title');
      }
    } else {
      // No profile selected - disable buttons
      const disabledMessage =
        t('child.navigation.selectProfileFirst') || 'Please select a profile first';
      if (libraryBtn) {
        libraryBtn.disabled = true;
        libraryBtn.classList.add('disabled');
        libraryBtn.setAttribute('aria-disabled', 'true');
        libraryBtn.title = disabledMessage;
      }
      if (achievementsBtn) {
        achievementsBtn.disabled = true;
        achievementsBtn.classList.add('disabled');
        achievementsBtn.setAttribute('aria-disabled', 'true');
        achievementsBtn.title = disabledMessage;
      }
      if (settingsBtn) {
        settingsBtn.disabled = true;
        settingsBtn.classList.add('disabled');
        settingsBtn.setAttribute('aria-disabled', 'true');
        settingsBtn.title = disabledMessage;
      }
    }
  }

  /**
   * Update language
   */
  updateLanguage(newLanguage) {
    this.currentLanguage = newLanguage;

    if (!this.element) return;

    // Update language-specific navigation icons
    const libraryBtn = this.element.querySelector('[data-route="library"]');
    const achievementsBtn = this.element.querySelector('[data-route="achievements"]');

    if (libraryBtn) {
      const img = libraryBtn.querySelector('.nav-icon-image');
      img.src = this.getNavigationIcon('library', newLanguage);
    }

    if (achievementsBtn) {
      const img = achievementsBtn.querySelector('.nav-icon-image');
      img.src = this.getNavigationIcon('achievements', newLanguage);
    }
  }

  /**
   * Hide navigation bar (for full-screen modes)
   */
  hide() {
    this.isHidden = true;
    if (this.element) {
      this.element.classList.add('hidden');
    }
  }

  /**
   * Show navigation bar
   */
  show() {
    this.isHidden = false;
    if (this.element) {
      this.element.classList.remove('hidden');
    }
  }

  /**
   * Enable reader mode styling (orange background)
   */
  enableReaderMode() {
    // Apply orange background to #app container
    const app = document.getElementById('app');
    if (app) {
      app.classList.add('reader-mode');
    }
  }

  /**
   * Disable reader mode styling (transparent background)
   */
  disableReaderMode() {
    // Remove orange background from #app container
    const app = document.getElementById('app');
    if (app) {
      app.classList.remove('reader-mode');
    }
  }

  /**
   * Enter filter mode — show Tassie + filter icons, hide standard nav items
   * Called when navigating to the library page
   */
  enterFilterMode() {
    if (this.isFilterMode) return;
    this.isFilterMode = true;

    if (!this.element) return;

    // Add filter-mode class to nav bar (hides standard items via CSS)
    this.element.classList.add('navigation-bar--filter-mode');

    // Render filter bar into the nav element
    this.filterBar.setProfileId(this.profileData?.id || '');
    this.filterBar.render(this.element);
  }

  /**
   * Exit filter mode — restore standard 3-icon nav
   * Called when navigating away from the library page
   */
  exitFilterMode() {
    if (!this.isFilterMode) return;
    this.isFilterMode = false;

    if (!this.element) return;

    // Remove filter bar
    this.filterBar.remove();

    // Remove filter-mode class (restores standard items via CSS)
    this.element.classList.remove('navigation-bar--filter-mode');
  }

  /**
   * Destroy navigation bar
   */
  destroy() {
    // Clean up filter bar if active
    if (this.isFilterMode) {
      this.filterBar.remove();
    }

    if (this.element && this.element.parentNode) {
      this.element.parentNode.removeChild(this.element);
    }
    this.element = null;
  }
}
